import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { bool, func, object, oneOfType, string, number } from "prop-types";
import { useHistory } from "react-router-dom";
import cogoToast from "cogo-toast";
import CustomButton from "../../../../components/CustomButton";
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  WhatsappShareButton,
  WhatsappIcon,
} from "react-share";

const GuestReferModal = ({ open, onClose, jobId, job }) => {
  const history = useHistory();
  const [step, setStep] = useState("choice");
  const [copied, setCopied] = useState(false);
  const textAreaRef = useRef(null);

  const baseUrl = process.env.REACT_APP_WEB_URL || window.location.origin;
  const guestShareUrl = jobId
    ? `${baseUrl}/job-lists-details/${jobId}/apply/no-ref`
    : "";

  let title = `Job Referral for ${job?.role_title || "this role"}`;
  if (job?.campany_name) {
    title += ` at ${job.campany_name}`;
  }
  const hashtags = ["workbrook", "job", "workbrookjobs"];

  useEffect(() => {
    if (!open) {
      setStep("choice");
      setCopied(false);
    }
  }, [open]);

  useEffect(() => {
    if (copied) {
      cogoToast.success("Link copied");
      setCopied(false);
    }
  }, [copied]);

  const handleClose = () => {
    setStep("choice");
    onClose();
  };

  const handleSignIn = () => {
    sessionStorage.setItem(
      "pageToVisit",
      JSON.stringify({
        accountType: localStorage.getItem("accountType"),
        path: `/home/${jobId}/refer`,
      })
    );
    history.push("/signin");
  };

  const copyToClipboard = () => {
    if (!textAreaRef.current) return;
    textAreaRef.current.select();
    document.execCommand("copy");
    setCopied(true);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="guest-refer-dialog-title"
    >
      <DialogTitle id="guest-refer-dialog-title" disableTypography>
        <div className="d-flex align-items-center justify-content-between">
          <h5 className="mb-0">{step === "choice" ? "Refer this job" : "Share as guest"}</h5>
          <IconButton aria-label="close" onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers>
        {step === "choice" ? (
          <div className="py-2">
            <p className="mb-3" style={{ color: "#444", lineHeight: 1.5 }}>
              How would you like to refer this role?
            </p>

            <div
              className="mb-3 p-3"
              style={{
                backgroundColor: "#f0f7ff",
                borderRadius: 8,
                border: "1px solid #c5daf5",
              }}
            >
              <strong style={{ color: "#1A2340" }}>Sign up / Sign in</strong>
              <p className="mb-0 mt-1" style={{ fontSize: 14, color: "#555" }}>
                Get a personal referral link tied to your profile. You can earn
                referral rewards when someone you refer is hired (when the job
                plan allows it).
              </p>
            </div>

            <div
              className="mb-4 p-3"
              style={{
                backgroundColor: "#FFF9E6",
                borderRadius: 8,
                border: "1px solid #ffeaa7",
              }}
            >
              <strong style={{ color: "#1A2340" }}>Continue as guest</strong>
              <p className="mb-0 mt-1" style={{ fontSize: 14, color: "#856404" }}>
                Share the job without an account. Guest shares are not linked to
                a profile and will not earn referral rewards.
              </p>
            </div>

            <CustomButton
              backgroundColor="#3A6ED4"
              color="#fff"
              text="Sign up / Sign in"
              handleClick={handleSignIn}
              additionalClass="mb-2 justify-content-center"
            />
            <CustomButton
              backgroundColor="#fff"
              color="#3A6ED4"
              borderColor="#3A6ED4"
              text="Continue as guest"
              handleClick={() => setStep("guest")}
              additionalClass="justify-content-center"
              style={{ border: "1px solid #3A6ED4" }}
            />
          </div>
        ) : (
          <div className="py-2">
            <div
              className="alert alert-warning text-center mb-4"
              style={{
                backgroundColor: "#fff3cd",
                borderColor: "#ffeaa7",
                color: "#856404",
                borderRadius: 8,
                padding: "12px 16px",
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <strong>Note:</strong> Guest referrals are not linked to a profile
              and will not earn rewards.{" "}
              <button
                type="button"
                className="btn btn-link p-0 align-baseline"
                style={{ fontSize: 14, color: "#3A6ED4" }}
                onClick={handleSignIn}
              >
                Sign up / Sign in
              </button>{" "}
              to get a personal referral link.
            </div>

            <h6 className="co-primary text-center mb-3">
              Share this job using the link below
            </h6>

            <textarea
              ref={textAreaRef}
              value={guestShareUrl}
              className="borderless w-100 copy-link"
              rows={3}
              readOnly
              onChange={() => {}}
            />

            <div className="d-flex mt-3 justify-content-center">
              <CustomButton
                backgroundColor="#3A6ED4"
                color="#fff"
                text="Copy Link"
                handleClick={copyToClipboard}
                additionalClass="justify-content-center"
                style={{ maxWidth: 200 }}
              />
            </div>

            <div className="mt-4 text-center">
              <h6>Share to Social Network</h6>
              <div>
                <FacebookShareButton
                  url={guestShareUrl}
                  quote={title}
                  className="mr-2"
                  hashtag="#workbrook #job"
                >
                  <FacebookIcon size={32} round />
                </FacebookShareButton>
                <TwitterShareButton
                  url={guestShareUrl}
                  title={title}
                  className="mr-2"
                  hashtags={hashtags}
                >
                  <TwitterIcon size={32} round />
                </TwitterShareButton>
                <LinkedinShareButton url={guestShareUrl} className="mr-2">
                  <LinkedinIcon size={32} round />
                </LinkedinShareButton>
                <WhatsappShareButton
                  url={`${title}  ${guestShareUrl}`}
                  className="mr-2"
                >
                  <WhatsappIcon size={32} round />
                </WhatsappShareButton>
              </div>
            </div>

            <div className="mt-4 text-center">
              <button
                type="button"
                className="btn btn-link"
                style={{ color: "#666" }}
                onClick={() => setStep("choice")}
              >
                Back
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

GuestReferModal.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  jobId: oneOfType([string, number]),
  job: object,
};

GuestReferModal.defaultProps = {
  jobId: null,
  job: null,
};

export default GuestReferModal;
